import { colors } from '@everlywell/leaves';
import Layout from 'components/Layout';
import {
  IRichTextContainer,
  RichTextContainer as RichTextSection,
} from 'containers/CaseStudyPage/RichTextContainer';
import { ITestimonialContainer } from 'containers/CaseStudyPage/TestimonialContainer';
import {
  HeroContainer as HeroSection,
  ISolutionsHeroContainer,
} from 'containers/Solutions/Hero';
import { IStatisticsContainer } from 'containers/Solutions/StatisticsContainer';
import { graphql } from 'gatsby';
import React, { LazyExoticComponent, lazy } from 'react';
import { IPartnerContainer } from 'utils/types/index';

import { SEO } from '../components/SEO';
const PartnerSection = lazy(() => import('containers/PartnerContainer'));
const StatisticsSection = lazy(
  () => import('containers/Solutions/StatisticsContainer'),
);
const BusinessTestimonialSection = lazy(
  () => import('containers/CaseStudyPage/TestimonialContainer'),
);

const components: {
  HeroSection: React.FunctionComponent<ISolutionsHeroContainer>;
  RichTextSection: React.FunctionComponent<IRichTextContainer>;
  BusinessTestimonialSection: LazyExoticComponent<
    React.FC<ITestimonialContainer>
  >;
  PartnerSection: LazyExoticComponent<React.FC<IPartnerContainer>>;
  StatisticsSection: LazyExoticComponent<React.FC<IStatisticsContainer>>;
} = {
  HeroSection,
  RichTextSection,
  PartnerSection,
  StatisticsSection,
  BusinessTestimonialSection,
};

type Props = {
  data: {
    caseStudyContent: {
      title: string;
      heroSection: {
        headline: string;
        description?: {
          description: string;
        };
        backgroundColor?: string;
      };
      sections: {
        componentName: string;
        headline: string;
        description?: { description: string };
        slug: string;
        title?: string;
        disclaimer: {
          internal: {
            content: string;
          };
        };
        listOfContentWithImages?: {
          title: string;
          heading: string;
          backgroundColor: string;
          description: {
            description: string;
            childMarkdownRemark: {
              html: string;
            };
          };
          slug: string;
          cta: {
            text: string;
            url: string;
          };
          image?: {
            title: string;
            gatsbyImageData: any;
          };
        }[];
      }[];
    };
  };
};

export const Head = () => <SEO />;

export const caseStudyPage = (props: Props) => {
  const { data } = props;
  const { caseStudyContent } = data;
  const { heroSection, sections, title } = caseStudyContent;

  return (
    <Layout>
      <HeroSection
        content={heroSection}
        title={title}
        headlineStyle={{ color: colors.white }}
      />
      {sections.map((section, i: number) => {
        // @ts-ignore
        if (components[section.componentName]) {
          return React.createElement(
            // @ts-ignore
            components[section.componentName],
            {
              content: section,
              key: `${section.componentName}-${i}`, // handles the case for duplicate section keys
            },
          );
        }
      })}
    </Layout>
  );
};

export default caseStudyPage;

export const data = graphql`
  query ($slug: String!) {
    caseStudyContent: contentfulResource(slug: { eq: $slug }) {
      title
      heroSection {
        headline
        description {
          description
        }
        componentName
        backgroundColor
        cta {
          text
          url
        }
        desktopImage {
          title
          gatsbyImageData(
            placeholder: BLURRED
            layout: FULL_WIDTH
            quality: 75
            resizingBehavior: FILL
            cropFocus: FACE
          )
        }
      }
      sections {
        title
        headline
        componentName
        slug
        backgroundColor
        disclaimer {
          internal {
            content
          }
        }
        sectionImage {
          title
          gatsbyImageData(placeholder: BLURRED, quality: 75)
        }
        cta {
          text
          url
        }
        richTextBody {
          raw
        }
        listOfContentWithImages {
          ... on ContentfulContentWithImage {
            title
            heading
            backgroundColor
            description {
              description
              childMarkdownRemark {
                html
              }
            }
            slug
            cta {
              text
              url
            }
            image {
              title
              gatsbyImageData(placeholder: BLURRED, quality: 75)
            }
          }
        }
      }
    }
  }
`;
